import type { ReactNode } from 'react';
import { Combobox } from '@/components/ui/combobox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { CredentialAssigneeOption, PrintedCredentialRow } from '@/types';

export const UNASSIGNED_VALUE = 'none';

// What both dialogs post. `assigneeType`/`assigneeId` are the pair the controller maps
// onto the three nullable assignee columns.
export type CredentialFormData = {
  slug: string;
  type: string;
  label: string;
  assigneeType: string;
  assigneeId: string | null;
};

export function emptyCredentialForm(): CredentialFormData {
  return {
    slug: '',
    type: 'hacker',
    label: '',
    assigneeType: UNASSIGNED_VALUE,
    assigneeId: null,
  };
}

export function credentialToForm(
  credential: PrintedCredentialRow,
): CredentialFormData {
  return {
    slug: credential.slug,
    type: credential.type,
    label: credential.label ?? '',
    assigneeType: credential.assignee?.type ?? UNASSIGNED_VALUE,
    assigneeId: credential.assignee?.id ?? null,
  };
}

function assigneeValue(
  assigneeType: string,
  assigneeId?: string | null,
): string {
  return assigneeType === 'none' || !assigneeId
    ? UNASSIGNED_VALUE
    : `${assigneeType}:${assigneeId}`;
}

// The Next dialogs used shadcn's react-hook-form wrappers, which are deliberately not
// ported (U2). This reproduces the markup FormItem/FormLabel/FormMessage rendered.
function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="grid gap-2">
      <Label data-error={error ? 'true' : undefined}>{label}</Label>
      {children}
      {error && <p className="text-destructive text-sm">{error}</p>}
    </div>
  );
}

interface CredentialFormFieldsProps {
  data: CredentialFormData;
  setData: (key: keyof CredentialFormData, value: string | null) => void;
  errors: Record<string, string | undefined>;
  isPending: boolean;
  assignableOptions: CredentialAssigneeOption[];
  credentialTypes: string[];
}

export function CredentialFormFields({
  data,
  setData,
  errors,
  isPending,
  assignableOptions,
  credentialTypes,
}: CredentialFormFieldsProps) {
  const currentAssignee = assigneeValue(data.assigneeType, data.assigneeId);

  return (
    <>
      <Field label="Card slug" error={errors.slug}>
        <Input
          placeholder="hacker-7"
          disabled={isPending}
          value={data.slug}
          onChange={(e) => setData('slug', e.target.value)}
        />
      </Field>

      <Field label="Type" error={errors.type}>
        <Select
          value={data.type}
          onValueChange={(value) => setData('type', value)}
          disabled={isPending}
        >
          <SelectTrigger>
            <SelectValue placeholder="Card type" />
          </SelectTrigger>
          <SelectContent>
            {credentialTypes.map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      <Field label="Label (optional)" error={errors.label}>
        <Input
          placeholder="Name printed on the card"
          disabled={isPending}
          value={data.label}
          onChange={(e) => setData('label', e.target.value)}
        />
      </Field>

      <Field label="Assigned to" error={errors.assigneeId}>
        <Combobox
          options={[
            { value: UNASSIGNED_VALUE, label: 'Unassigned (blank card)' },
            ...assignableOptions,
          ]}
          value={currentAssignee}
          disabled={isPending}
          placeholder="Unassigned (blank card)"
          searchPlaceholder="Search hackers, mentors, external..."
          emptyText="Nobody found."
          onValueChange={(value) => {
            if (!value || value === UNASSIGNED_VALUE) {
              setData('assigneeType', UNASSIGNED_VALUE);
              setData('assigneeId', null);
              return;
            }

            const [type, id] = value.split(':');
            setData('assigneeType', type);
            setData('assigneeId', id);
          }}
        />
      </Field>
    </>
  );
}
